/* ============================================================
   ADMIN — butiran satu Partner (/admin/partners → View)
   ============================================================
   Arahan user 2026-10-26: "admin still boleh nampak apa yg partner tengah
   buat. semua termasuk draft, request or approved". BACA SAHAJA:
     · semua module Partner + status (Draft / In review / Changes requested / Approved)
     · buka module → baca master prompt mana-mana versi (AdminPartnerReview,
       Approve/Request hanya bila versi itu menunggu review)
     · ujian lab terkini Partner + hasilnya
   ⚠️ Draf yang BELUM disimpan hanya wujud dalam browser Partner — server tidak
      pernah menerimanya, jadi tidak boleh dipapar di sini.
   ============================================================ */
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Box, Loader2, RefreshCw } from 'lucide-react';
import { Btn, Badge, StatTile, Segmented, EmptyState } from '../design/ui';
import { panggilPartner, masaLalu, statusModul } from '../services/partnerStudioService';
import AdminPartnerReview from './AdminPartnerReview';
import { Kolum } from './PartnerTestLab';

export default function AdminPartnerButiran({ partner, onKembali, onSelesai }) {
    const [data, setData] = useState(null);
    const [ralat, setRalat] = useState('');
    const [tapis, setTapis] = useState('semua');
    const [buka, setBuka] = useState(null);          // module yang sedang dibaca
    const [larian, setLarian] = useState(null);      // { sibuk, ralat, hasil }

    const muat = useCallback(async () => {
        try {
            setData(await panggilPartner('adminPartner', { uid: partner.uid }));
            setRalat('');
        } catch (e) { setRalat(e?.message || 'Could not load this partner.'); }
    }, [partner.uid]);
    useEffect(() => { (async () => { await muat(); })(); }, [muat]);

    if (buka) {
        return (
            <AdminPartnerReview
                key={`${buka.id}`}
                item={{ uid: partner.uid, modId: buka.id, nama: buka.nama }}
                labelKembali={partner.email}
                onKembali={() => setBuka(null)}
                onSelesai={(teks) => { setBuka(null); onSelesai(teks); }}
            />
        );
    }

    const bukaLarian = async (id) => {
        setLarian({ sibuk: true, ralat: '', hasil: null });
        try {
            const { larian: r } = await panggilPartner('adminLarianBaca', { uid: partner.uid, id });
            setLarian({ sibuk: false, ralat: '', hasil: r });
        } catch (e) { setLarian({ sibuk: false, ralat: e?.message || 'Could not open that test.', hasil: null }); }
    };

    const semua = (data?.modules || []).map((m) => ({ m, st: statusModul(m) }));
    const bil = (k) => semua.filter((x) => x.st.kunci === k).length;
    const dipapar = tapis === 'semua' ? semua : semua.filter((x) => x.st.kunci === tapis);

    return (
        <div>
            <Btn variant="quiet" size="sm" icon={<ArrowLeft size={14} />} onClick={onKembali}>Partners</Btn>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '8px 0 14px' }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <h1 className="s-title" style={{ margin: 0 }}>{partner.email}</h1>
                        {data && <Badge tone={data.partner.status === 'aktif' ? 'ok' : 'stop'}>{data.partner.status === 'aktif' ? 'Active' : 'Suspended'}</Badge>}
                    </div>
                    <p className="s-subtitle">Everything this partner has saved — drafts, in review and approved. Read only. Unsaved edits in their browser aren't visible.</p>
                </div>
                <Btn size="sm" variant="ghost" icon={<RefreshCw size={14} />} onClick={muat}>Refresh</Btn>
            </div>

            {ralat && <p className="s-hint" style={{ color: 'var(--s-stop)' }}>{ralat}</p>}
            {!data && !ralat && <p className="s-hint"><Loader2 size={14} className="animate-spin" style={{ verticalAlign: -2 }} /> Loading…</p>}

            {data && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 14 }}>
                        <StatTile label="Modules" value={semua.length} />
                        <StatTile label="In review" value={bil('menunggu')} />
                        <StatTile label="Approved" value={bil('lulus')} />
                        <StatTile label="Lab tests today" value={`${data.kuota.guna} / ${data.kuota.had}`} />
                    </div>

                    {semua.length === 0 ? <EmptyState title="No modules yet">This partner hasn't created a module.</EmptyState> : (
                        <>
                            <div style={{ overflowX: 'auto', marginBottom: 10 }}>
                                <Segmented value={tapis} onChange={setTapis} options={[
                                    { value: 'semua', label: `All ${semua.length}` },
                                    { value: 'menunggu', label: `In review ${bil('menunggu')}` },
                                    { value: 'perlu_ubah', label: `Changes requested ${bil('perlu_ubah')}` },
                                    { value: 'lulus', label: `Approved ${bil('lulus')}` },
                                    { value: 'draf', label: `Draft ${bil('draf')}` },
                                ]} />
                            </div>
                            {dipapar.length === 0 ? <p className="s-hint">No modules here.</p> : (
                                <div className="s-card" style={{ marginBottom: 14 }}>
                                    <div className="s-list">
                                        {dipapar.map(({ m, st }) => (
                                            <div key={m.id} className="s-list__row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px' }}>
                                                <Box size={16} style={{ color: 'var(--s-ink-3)', flexShrink: 0 }} aria-hidden="true" />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div className="s-cardname">{m.nama}</div>
                                                    <div className="s-dim" style={{ fontSize: 12 }}>
                                                        {m.versiSemasa ? `version ${m.versiSemasa} · saved ${masaLalu(m.dikemaskini)}` : 'nothing saved yet'}
                                                        {m.modulRasmiId ? ` · ${m.modulRasmiId}` : ''}
                                                        {m.drafDikemaskini ? ` · unsaved draft ${masaLalu(m.drafDikemaskini)}` : ''}
                                                    </div>
                                                    {st.ket && <div className="s-dim" style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{st.ket}</div>}
                                                </div>
                                                <Badge tone={st.tone}>{st.label}</Badge>
                                                <Btn size="sm" variant="ghost" disabled={!m.versiSemasa && !m.drafDikemaskini} title={m.versiSemasa || m.drafDikemaskini ? undefined : 'Nothing saved yet'} onClick={() => setBuka(m)}>Open</Btn>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {larian && <div style={{ marginBottom: 12 }}><Kolum tunjukKos format={(data?.modules || []).find((m) => m.id === larian.hasil?.modId)?.format || null} tajuk={larian.hasil ? `${larian.hasil.modNama} · ${larian.hasil.produkNama} · ${larian.hasil.sumber === 'draf' ? 'draft' : `v${larian.hasil.versi}`}` : 'Test'} {...larian} /></div>}
                    {larian?.hasil?.draf && (
                        <details className="s-card" style={{ padding: '10px 14px', marginBottom: 12 }}>
                            <summary className="s-hint" style={{ cursor: 'pointer' }}>Draft prompt used in this test</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'var(--s-mono)', fontSize: 12.5, margin: '8px 0 0' }}>{larian.hasil.draf}</pre>
                        </details>
                    )}

                    <div className="s-card">
                        <div className="s-sect" style={{ gap: 0 }}><p className="s-sect__title">Recent lab tests ({data.larian.length}) — click one to see the result</p></div>
                        {data.larian.length === 0 ? <p className="s-hint" style={{ padding: '0 16px 12px' }}>No tests yet.</p> : (
                            <div className="s-list">
                                {data.larian.map((r) => (
                                    <button key={r.id} type="button" className="s-list__row" onClick={() => bukaLarian(r.id)}
                                        style={{ display: 'flex', gap: 10, alignItems: 'center', width: '100%', textAlign: 'left', padding: '9px 16px', background: 'none', border: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}>
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {r.modNama} · {r.produkNama} · {r.sumber === 'draf' ? 'draft' : `v${r.versi}`} · mode {r.mode}{r.vision ? ' · vision' : ''} · {r.model}
                                        </span>
                                        <span className="s-dim">{masaLalu(r.masa)}</span>
                                        <Badge tone={r.status === 'ok' ? 'ok' : 'warn'}>{r.status === 'ok' ? `${r.bilSet} sets` : 'check'}</Badge>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
